import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import type { MyWork, MyWorkItem, PersonRow, Priority } from '../lib/types';
import { formatMinutes } from '../lib/types';
import { useSession } from '../lib/session';
import { BillingBadge, PriorityBadge, StatusDot } from '../components/Badges';
import { SlaBadge } from '../components/SlaBadge';
import { formatDate, formatDayLabel } from '../lib/dates';
import type { MyWorkOrder, MyWorkView } from '../lib/myWorkView';
import { DEFAULT_VIEW, ORDER_LABELS, isNarrowed, keep, ordered } from '../lib/myWorkView';
import type { WorkSource } from '../lib/workSource';
import { WORK_SOURCES, WORK_SOURCE_LABELS } from '../lib/workSource';

const VIEW_KEY = 'hub.myWorkView';
const PRIORITY_CHOICES: Priority[] = ['critical', 'high', 'medium', 'low'];
const PRIORITY_LABELS: Record<Priority, string> = { critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low', none: 'None' };

function toggled<T>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((entry) => entry !== value) : [...list, value];
}

/** The calendar date here, so "due today" means today where the person actually is. */
function localToday(): string {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
}

/**
 * One person's work in one place.
 *
 * A board answers "how is this project going". This answers the question somebody actually
 * has at nine in the morning — what is on me — which otherwise meant opening every board in
 * turn and reading past everyone else's cards.
 */
export function MyWorkPage() {
  const { session } = useSession();
  const [work, setWork] = useState<MyWork | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [personId, setPersonId] = useState<string>('');
  const [people, setPeople] = useState<PersonRow[]>([]);
  const [showReported, setShowReported] = useState(false);
  /** Remembered per browser: how somebody likes their own list is theirs to keep. */
  const [view, setView] = useState<MyWorkView>(() => {
    try {
      const saved = localStorage.getItem(VIEW_KEY);
      return saved ? { ...DEFAULT_VIEW, ...JSON.parse(saved) } : DEFAULT_VIEW;
    } catch {
      return DEFAULT_VIEW;
    }
  });

  function changeView(next: MyWorkView) {
    setView(next);
    try {
      localStorage.setItem(VIEW_KEY, JSON.stringify(next));
    } catch {
      /* private browsing */
    }
  }

  const today = useMemo(localToday, []);
  const isAdmin = !!session?.person.isAdmin;

  const load = useCallback(() => {
    const query = new URLSearchParams({ today });
    if (personId) query.set('personId', personId);

    setError(null);
    api
      .get<MyWork>(`/me/work?${query.toString()}`)
      .then(setWork)
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Could not load your work'));
  }, [personId, today]);

  useEffect(load, [load]);

  // Only an administrator can look at anybody else, so only they need the picker.
  useEffect(() => {
    if (!isAdmin) return;
    api
      .get<PersonRow[]>('/people')
      .then(setPeople)
      .catch(() => setPeople([]));
  }, [isAdmin]);

  if (error) return <div className="callout">{error}</div>;
  if (!work) return <div className="empty">Loading…</div>;

  const { summary } = work;
  const nothing = summary.open === 0 && summary.awaitingClose === 0;

  const companiesOnList = [...new Map(work.buckets.flatMap((b) => b.items).map((i) => [i.company.id, i.company])).values()].sort(
    (a, b) => a.name.localeCompare(b.name),
  );
  const visible = (items: MyWorkItem[]) => items.filter((item) => keep(item, view, work.today));
  const open = work.buckets.filter((bucket) => bucket.key !== 'finished').flatMap((bucket) => bucket.items);
  const finished = work.buckets.find((bucket) => bucket.key === 'finished');
  const shownCount = visible(work.buckets.flatMap((bucket) => bucket.items)).length;

  return (
    <>
      <div className="page-head">
        <div>
          <div className="eyebrow">{work.viewingSelf ? 'My work' : 'Someone else’s work'}</div>
          <h1>{work.viewingSelf ? 'What is on you' : work.person.name}</h1>
          <p className="subtitle">
            Everything assigned to {work.viewingSelf ? 'you' : work.person.name}, across
            every company and project — soonest first. {longDate(work.today)}.
          </p>
        </div>

        {work.canViewOthers && people.length > 0 && (
          <label className="row" style={{ gap: 8 }}>
            <span className="dim" style={{ fontSize: 12 }}>
              Showing
            </span>
            <select value={personId} onChange={(event) => setPersonId(event.target.value)}>
              <option value="">Me</option>
              {people
                .filter((person) => person.id !== session?.person.id)
                .map((person) => (
                  <option key={person.id} value={person.id}>
                    {person.name}
                  </option>
                ))}
            </select>
          </label>
        )}
      </div>

      <div className="stat-row mb-2">
        <Stat label="To do" value={summary.open} />
        <Stat label="Overdue" value={summary.overdue} accent={summary.overdue > 0} />
        <Stat label="Due today" value={summary.dueToday} />
        <Stat label="Blocked" value={summary.blocked} accent={summary.blocked > 0} />
        <Stat label="Waiting on others" value={summary.waiting} />
        {summary.slaBreached > 0 ? (
          <Stat
            label="SLA breached"
            value={summary.slaBreached}
            accent
            note={summary.slaAtRisk > 0 ? `${summary.slaAtRisk} also at risk` : undefined}
          />
        ) : (
          summary.slaAtRisk > 0 && <Stat label="SLA at risk" value={summary.slaAtRisk} />
        )}
        <Stat
          label="Logged today"
          value={formatMinutes(summary.loggedTodayMinutes)}
          note={`${formatMinutes(summary.loggedWeekMinutes)} this week`}
          small
        />
      </div>

      {!nothing && (
        <ViewControls
          view={view}
          onChange={changeView}
          companies={companiesOnList}
          shown={shownCount}
          total={work.buckets.reduce((sum, bucket) => sum + bucket.items.length, 0)}
        />
      )}

      {nothing ? (
        <div className="card">
          <div className="empty">
            <h3>{work.viewingSelf ? 'Nothing is assigned to you' : `Nothing is assigned to ${work.person.name}`}</h3>
            <p>
              {work.viewingSelf
                ? 'When somebody puts a task on you it appears here, with the ones that are late at the top.'
                : 'Assign work from a project board and it will show up here.'}
            </p>
          </div>
        </div>
      ) : (
        <div className="stack">
          {shownCount === 0 && (
            <div className="card">
              <div className="empty">
                <h3>Nothing matches</h3>
                <p>Everything on your list is hidden by the filters above.</p>
                <button type="button" className="btn btn-primary" onClick={() => changeView({ ...DEFAULT_VIEW, order: view.order })}>
                  Clear the filters
                </button>
              </div>
            </div>
          )}

          {view.order === 'due'
            ? work.buckets
                .map((bucket) => ({ ...bucket, items: visible(bucket.items) }))
                .filter((bucket) => bucket.items.length > 0)
                .map((bucket) => (
                  <div className="card" key={bucket.key}>
                    <div className="card-head">
                      <h2>
                        {bucket.label}
                        {bucket.key === 'overdue' && ' '}
                        {bucket.key === 'overdue' && <span className="badge badge-accent">{bucket.items.length}</span>}
                      </h2>
                      <span className="dim" style={{ fontSize: 12 }}>
                        {bucket.hint || `${bucket.items.length}`}
                      </span>
                    </div>
                    <div>
                      {bucket.items.map((item) => (
                        <WorkRow key={item.id} item={item} today={work.today} />
                      ))}
                    </div>
                  </div>
                ))
            : (() => {
                const list = ordered(visible(open), view.order, work.today);
                const done = visible(finished?.items ?? []);
                return (
                  <>
                    {list.length > 0 && (
                      <div className="card">
                        <div className="card-head">
                          <h2>{view.order === 'urgency' ? 'In the order to do them' : 'Least time left first'}</h2>
                          <span className="dim" style={{ fontSize: 12 }}>
                            {view.order === 'urgency'
                              ? 'Priority first, then whichever has least time left'
                              : 'The due date or the SLA clock, whichever is nearer'}
                          </span>
                        </div>
                        <div>
                          {list.map((item) => (
                            <WorkRow key={item.id} item={item} today={work.today} />
                          ))}
                        </div>
                      </div>
                    )}
                    {finished && done.length > 0 && (
                      <div className="card">
                        <div className="card-head">
                          <h2>{finished.label}</h2>
                          <span className="dim" style={{ fontSize: 12 }}>
                            {finished.hint || `${done.length}`}
                          </span>
                        </div>
                        <div>
                          {done.map((item) => (
                            <WorkRow key={item.id} item={item} today={work.today} />
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}

          {work.reported.length > 0 && (
            <div className="card">
              <div className="card-head">
                <h2>{work.viewingSelf ? 'Raised by you' : `Raised by ${work.person.name}`}</h2>
                <button type="button" className="btn btn-quiet btn-sm" onClick={() => setShowReported((v) => !v)}>
                  {showReported ? 'Hide' : `Show ${work.reported.length}`}
                </button>
              </div>
              {showReported && (
                <div>
                  {work.reported.map((item) => (
                    <WorkRow key={item.id} item={item} today={work.today} showAssignee />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}

/**
 * How the list is ordered and narrowed. Every filter that is on shows again as a chip with a
 * cross, so any one of them can be taken off without starting again.
 */
function ViewControls({
  view,
  onChange,
  companies,
  shown,
  total,
}: {
  view: MyWorkView;
  onChange: (next: MyWorkView) => void;
  companies: Array<{ id: string; name: string }>;
  shown: number;
  total: number;
}) {
  const set = <K extends keyof MyWorkView>(key: K, value: MyWorkView[K]) => onChange({ ...view, [key]: value });
  const chips: Array<{ key: string; label: string; remove: () => void }> = [
    ...view.priorities.map((priority) => ({
      key: `p-${priority}`,
      label: PRIORITY_LABELS[priority],
      remove: () => set('priorities', view.priorities.filter((p) => p !== priority)),
    })),
    ...view.sources.map((source) => ({
      key: `s-${source}`,
      label: WORK_SOURCE_LABELS[source],
      remove: () => set('sources', view.sources.filter((s) => s !== source)),
    })),
    ...(view.company
      ? [
          {
            key: 'company',
            label: companies.find((c) => c.id === view.company)?.name ?? 'One company',
            remove: () => set('company', null),
          },
        ]
      : []),
    ...(view.overdueOnly ? [{ key: 'late', label: 'Late only', remove: () => set('overdueOnly', false) }] : []),
    ...(view.atRiskOnly ? [{ key: 'risk', label: 'SLA at risk', remove: () => set('atRiskOnly', false) }] : []),
  ];

  return (
    <div className="card card-pad mb-2">
      <div className="row wrap" style={{ gap: 14, alignItems: 'center' }}>
        <div className="row wrap" style={{ gap: 6 }} role="group" aria-label="Order">
          <span className="field-label" style={{ margin: 0 }}>
            Order
          </span>
          {(Object.keys(ORDER_LABELS) as MyWorkOrder[]).map((order) => (
            <button
              key={order}
              type="button"
              className={view.order === order ? 'chip-toggle is-on' : 'chip-toggle'}
              aria-pressed={view.order === order}
              onClick={() => set('order', order)}
            >
              {ORDER_LABELS[order]}
            </button>
          ))}
        </div>

        <div className="row wrap" style={{ gap: 6 }} role="group" aria-label="Priority">
          <span className="field-label" style={{ margin: 0 }}>
            Priority
          </span>
          {PRIORITY_CHOICES.map((priority) => (
            <button
              key={priority}
              type="button"
              className={view.priorities.includes(priority) ? 'chip-toggle is-on' : 'chip-toggle'}
              aria-pressed={view.priorities.includes(priority)}
              onClick={() => set('priorities', toggled(view.priorities, priority))}
            >
              {PRIORITY_LABELS[priority]}
            </button>
          ))}
        </div>

        <div className="row wrap" style={{ gap: 6 }} role="group" aria-label="Came in as">
          <span className="field-label" style={{ margin: 0 }}>
            Came in as
          </span>
          {WORK_SOURCES.map((source: WorkSource) => (
            <button
              key={source}
              type="button"
              className={view.sources.includes(source) ? 'chip-toggle is-on' : 'chip-toggle'}
              aria-pressed={view.sources.includes(source)}
              onClick={() => set('sources', toggled(view.sources, source))}
            >
              {WORK_SOURCE_LABELS[source]}
            </button>
          ))}
        </div>

        <div className="row wrap" style={{ gap: 6 }}>
          <button
            type="button"
            className={view.overdueOnly ? 'chip-toggle is-on' : 'chip-toggle'}
            aria-pressed={view.overdueOnly}
            onClick={() => set('overdueOnly', !view.overdueOnly)}
          >
            Late only
          </button>
          <button
            type="button"
            className={view.atRiskOnly ? 'chip-toggle is-on' : 'chip-toggle'}
            aria-pressed={view.atRiskOnly}
            onClick={() => set('atRiskOnly', !view.atRiskOnly)}
          >
            SLA at risk
          </button>
          {companies.length > 1 && (
            <select
              value={view.company ?? ''}
              onChange={(event) => set('company', event.target.value || null)}
              aria-label="Company"
              style={{ width: 'auto' }}
            >
              <option value="">Every company</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {chips.length > 0 && (
        <div className="row wrap" style={{ gap: 6, marginTop: 10, alignItems: 'center' }}>
          <span className="dim" style={{ fontSize: 12 }}>
            Showing {shown} of {total}:
          </span>
          {chips.map((chip) => (
            <button key={chip.key} type="button" className="filter-chip" onClick={chip.remove} title={`Remove ${chip.label}`}>
              {chip.label} <span aria-hidden>×</span>
            </button>
          ))}
          {isNarrowed(view) && (
            <button type="button" className="btn btn-quiet btn-sm" onClick={() => onChange({ ...DEFAULT_VIEW, order: view.order })}>
              Clear all
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** A row you can decide from without opening it: what, where, how late, and whose move. */
function WorkRow({
  item,
  today,
  showAssignee = false,
}: {
  item: MyWorkItem;
  today: string;
  showAssignee?: boolean;
}) {
  return (
    <Link
      to={`/projects/${item.project.id}/board?task=${item.id}`}
      className={item.completedAt ? 'result-row is-done' : 'result-row'}
    >
      <div className="row wrap" style={{ gap: 8 }}>
        <span className="key">{item.key}</span>
        <span className="grow" style={{ minWidth: 0 }}>
          {item.title}
        </span>
        {item.sla && <SlaBadge sla={item.sla} />}
        {item.status && <StatusDot color={item.status.color} label={item.status.label} />}
      </div>

      <div className="row wrap dim" style={{ gap: 10, fontSize: 11, marginTop: 3 }}>
        <span>
          {item.company.name} · {item.project.name}
        </span>
        <PriorityBadge priority={item.priority} />
        <DueLabel due={item.dueDate} today={today} done={!!item.completedAt} />
        {showAssignee && <span>{item.assigneeNames ?? 'nobody yet'}</span>}
        {item.bucket === 'waiting'
          ? item.blocked?.reason && <span className="badge">waiting — {item.blocked.reason}</span>
          : item.blocked && <span className="badge badge-warn">blocked{item.blocked.reason ? ` — ${item.blocked.reason}` : ''}</span>}
        {item.merge && (
          <span className="badge">
            !{item.merge.iid} {item.merge.state}
            {item.merge.pipelineStatus ? ` · ${item.merge.pipelineStatus}` : ''}
          </span>
        )}
        {item.reopenCount > 0 && <span className="badge">↻{item.reopenCount}</span>}
        <BillingBadge exception={item.billingException} />
        {item.minutesLogged > 0 && (
          <span className="mono">
            {formatMinutes(item.minutesLogged)}
            {item.estimateMinutes ? ` of ${formatMinutes(item.estimateMinutes)}` : ' logged'}
          </span>
        )}
      </div>
    </Link>
  );
}

/** "Fri 04/09/2026" — the weekday is worth having on a heading; the date is dd/mm/yyyy. */
function longDate(date: string): string {
  return formatDayLabel(date, { year: true });
}

/** Says how late, or how soon, rather than making the reader work out a date. */
function DueLabel({ due, today, done }: { due: string | null; today: string; done: boolean }) {
  if (!due) return <span className="dim">no due date</span>;

  const days = Math.round((Date.parse(`${due}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86_400_000);
  const text =
    days === 0 ? 'due today' : days === 1 ? 'due tomorrow' : days > 0 ? `due in ${days} days` : `${-days} days late`;

  return (
    <span className="mono" style={days < 0 && !done ? { color: 'var(--accent)' } : undefined} title={`Due ${formatDate(due)}`}>
      {done ? `due ${formatDate(due)}` : text}
    </span>
  );
}

function Stat({
  label,
  value,
  accent,
  small,
  note,
}: {
  label: string;
  value: string | number;
  accent?: boolean;
  small?: boolean;
  note?: string;
}) {
  return (
    <div className="stat">
      <div className="stat-label">{label}</div>
      <div
        className="stat-value"
        style={{ ...(accent ? { color: 'var(--accent)' } : {}), ...(small ? { fontSize: 20 } : {}) }}
      >
        {value}
      </div>
      {note && (
        <div className="dim" style={{ fontSize: 11 }}>
          {note}
        </div>
      )}
    </div>
  );
}
